'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import TrustpilotWidget from '@/components/TrustpilotWidget';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'How Non-Fault Claims Work', href: '/how-non-fault-claims-work' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'FAQ', href: '/faq' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact', href: '/contact' },
  { label: 'Track Booking', href: '/track-booking' },
];

const serviceLinks = [
  { label: 'Car-O-Liner Chassis Alignment', href: '/services/chassis-alignment' },
  { label: 'Airbag & SRS Repair', href: '/services/airbag-srs' },
  { label: 'Module Coding (Mercedes/BMW)', href: '/services/module-coding' },
  { label: 'Body Repairs', href: '/services/body-repairs' },
];

const legalLinks = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Service', href: '/terms-of-service' },
];

export default function Footer() {
  return (
    <footer className="bg-[#121212] border-t-2 border-[#CC1A1A]">
      <div className="max-w-8xl mx-auto px-6 pt-14 pb-10">

        {/* Main grid: logo column + 4 content columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

          {/* Logo + tagline column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link href="/" aria-label="No Limit Auto Body Specialist home">
              <Image
                src="/assets/images/logo_black_charcoal-1773255396754.png"
                alt="No Limit Auto Body Specialist logo"
                height={40}
                width={120}
                className="object-contain mb-4"
                style={{ height: '40px', width: 'auto' }}
              />
            </Link>
            <p className="font-dm-sans text-[13px] text-white/45 leading-relaxed mb-4">
              Manchester&apos;s specialist bodyshop for non-fault claims and German vehicle repairs.
            </p>
            {/* Trustpilot Micro TrustScore badge */}
            <div style={{ background: '#121212', paddingTop: '8px' }}>
              <TrustpilotWidget templateId="5419b637fa0340045cd0c936" height="20px" />
            </div>
          </div>

          {/* Column 1 — Quick Links */}
          <div>
            <h3 className="font-dm-sans text-[11px] font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-5">
              Quick Links
            </h3>
            <nav aria-label="Footer quick links navigation">
              <ul className="space-y-1">
                {quickLinks?.map((link) => (
                  <li key={link?.label}>
                    <Link
                      href={link?.href}
                      className="font-dm-sans text-[14px] text-white/45 hover:text-[#CC1A1A] transition-colors duration-200 min-h-[44px] flex items-center py-1"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Column 2 — Our Services */}
          <div>
            <h3 className="font-dm-sans text-[11px] font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-5">
              Our Services
            </h3>
            <nav aria-label="Footer services navigation">
              <ul className="space-y-1">
                {serviceLinks?.map((link) => (
                  <li key={link?.label}>
                    <Link
                      href={link?.href}
                      className="font-dm-sans text-[14px] text-white/45 hover:text-[#CC1A1A] transition-colors duration-200 min-h-[44px] flex items-center py-1"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Column 3 — Contact */}
          <div>
            <h3 className="font-dm-sans text-[11px] font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-5">
              Contact
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:01614001687"
                  className="flex items-start gap-3 font-dm-sans text-[14px] text-white/45 hover:text-[#CC1A1A] transition-colors duration-200 py-1"
                  aria-label="Call us on 0161 400 1687"
                >
                  <span className="text-[#CC1A1A] shrink-0 mt-0.5">📞</span>
                  0161 400 1687
                </a>
              </li>
              <li>
                <a
                  href="mailto:contactus@nolimitauto.co.uk"
                  className="flex items-start gap-3 font-dm-sans text-[14px] text-white/45 hover:text-[#CC1A1A] transition-colors duration-200 py-1"
                  aria-label="Email us at contactus@nolimitauto.co.uk"
                >
                  <span className="text-[#CC1A1A] shrink-0 mt-0.5">✉️</span>
                  contactus@nolimitauto.co.uk
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 py-1">
                  <span className="text-[#CC1A1A] shrink-0 mt-0.5">📍</span>
                  <address className="not-italic font-dm-sans text-[14px] text-white/45 leading-relaxed">
                    No Limit Auto Body Specialist Limited · 17 Stanley Street · Manchester · M8 8SH · United Kingdom
                  </address>
                </div>
              </li>
            </ul>
          </div>

          {/* Column 4 — Legal */}
          <div>
            <h3 className="font-dm-sans text-[11px] font-bold uppercase tracking-[0.2em] text-[#CC1A1A] mb-5">
              Legal
            </h3>
            <nav aria-label="Footer legal navigation">
              <ul className="space-y-1">
                {legalLinks?.map((link) => (
                  <li key={link?.label}>
                    <Link
                      href={link?.href}
                      className="font-dm-sans text-[14px] text-white/45 hover:text-[#CC1A1A] transition-colors duration-200 min-h-[44px] flex items-center py-1"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-4 space-y-2">
              <p className="font-dm-sans text-[12px] text-white/25 leading-relaxed">
                © 2026 NO LIMIT AUTO BODY SPECIALIST. All rights reserved.
              </p>
              <p className="font-dm-sans text-[12px] text-white/25">
                Registered in England &amp; Wales
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.06] pt-8">
          <p className="font-dm-sans text-[12px] text-white/20 text-center">
            No Limit Auto Body Specialist Limited · 17 Stanley Street · Manchester · M8 8SH · United Kingdom
          </p>
        </div>
      </div>
    </footer>
  );
}